import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { API, axios } from "@/api";
import { Cake, Truck, AlertTriangle, Loader2 } from "lucide-react";
import { motion } from "framer-motion";

export default function ForkScreen() {
  const { user, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(null);
  const [showVendorWarning, setShowVendorWarning] = useState(false);

  // iter315 — admins skip the fork entirely. They have neither
  // ``onboarding_path`` nor ``onboarding_complete`` by seed design;
  // sending them down either branch traps them on /onboarding/user.
  if (user?.is_admin) {
    navigate("/", { replace: true });
    return null;
  }

  // If onboarding path already set, redirect
  if (user?.onboarding_path === "user") {
    if (!user?.onboarding_complete) {
      navigate("/onboarding/user", { replace: true });
    } else {
      navigate("/", { replace: true });
    }
    return null;
  }
  if (user?.onboarding_path === "vendor") {
    if (!user?.onboarding_complete) {
      navigate("/onboarding/vendor", { replace: true });
    } else {
      navigate("/vendor/dashboard", { replace: true });
    }
    return null;
  }

  const handleChoice = async (path) => {
    setLoading(path);
    try {
      await axios.post(`${API}/auth/onboarding-path`, { path });
      if (refreshUser) await refreshUser();
      navigate(path === "user" ? "/onboarding/user" : "/onboarding/vendor");
    } catch (e) {
      console.error("Fork choice failed:", e);
    } finally {
      setLoading(null);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white flex items-center justify-center px-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-md"
      >
        <div className="text-center mb-8">
          <h1 className="text-2xl font-bold text-slate-800">Welcome to BirthdayDeaLight</h1>
          <p className="text-slate-500 mt-2">Your birthday. Your city. Your deals.</p>
        </div>

        <div className="space-y-4">
          {/* User Path */}
          <button
            onClick={() => handleChoice("user")}
            disabled={loading}
            className="w-full bg-white rounded-2xl border-2 border-slate-200 hover:border-sky-400 p-6 text-left transition-all group disabled:opacity-50"
            data-testid="fork-user-btn"
          >
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 rounded-xl bg-sky-50 group-hover:bg-sky-100 flex items-center justify-center transition-colors">
                {loading === "user" ? (
                  <Loader2 className="w-7 h-7 text-sky-500 animate-spin" />
                ) : (
                  <Cake className="w-7 h-7 text-sky-500" />
                )}
              </div>
              <div>
                <p className="text-lg font-bold text-slate-800">I'm here for my birthday</p>
                <p className="text-sm text-slate-500 mt-0.5">Find free birthday treats and deals near you</p>
              </div>
            </div>
          </button>

          {/* Vendor Path */}
          <button
            onClick={() => handleChoice("vendor")}
            disabled={loading}
            className="w-full bg-white rounded-2xl border-2 border-slate-200 hover:border-amber-400 p-6 text-left transition-all group disabled:opacity-50"
            data-testid="fork-vendor-btn"
          >
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 rounded-xl bg-amber-50 group-hover:bg-amber-100 flex items-center justify-center transition-colors">
                {loading === "vendor" ? (
                  <Loader2 className="w-7 h-7 text-amber-500 animate-spin" />
                ) : (
                  <Truck className="w-7 h-7 text-amber-500" />
                )}
              </div>
              <div>
                <p className="text-lg font-bold text-slate-800">I'm a food truck or restaurant</p>
                <p className="text-sm text-slate-500 mt-0.5">Get discovered by birthday celebrants in your area</p>
              </div>
            </div>
          </button>
        </div>

        <p className="text-center text-xs text-slate-400 mt-6">This choice determines your experience and cannot be changed later.</p>
      </motion.div>
    </div>
  );
}
